import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Button,
  CashSpecialIcon,
  Layout,
  PageTitle,
  RoundedCheckIcon,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { FlowHeroMark } from "../components/FlowHeroMark";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { hasOfacDisambiguation } from "../utils/operationStatus";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { RequireActiveClerkSession } from "../hooks/useRequireActiveClerkSession";
import { formatLabeledAmount } from "../utils";
import {
  bindDeferredPayout,
  getDeferredQuote,
  getWidgetApiError,
  isDeadLinkError,
  type IOperationResponse,
  type IQuoteResponse,
} from "../services/api";
import {
  injectOfacActionRequired,
  isMockOfacEnabled,
} from "../mocks/ofacDisambiguation";
import { getEvents, safeCapture } from "../analytics";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { paymentType } from "../interfaces/payment";
import { clearPhoneSkip } from "../utils/phoneGate";
import {
  isOriginatorClosedReason,
  parseBindError,
  type ClaimBlockReason,
} from "../utils/bindErrors";
import {
  BIND_SUBJECT_BLOCKED_CODE,
  originatorClosedTerminalState,
  subjectBlockedTerminalState,
} from "../auth/resume";

/**
 * Commit screen. Transfer prepares the rail and stores the quote; this page
 * binds the deferred payout while "Processing your payout" is on screen.
 */
export const SendingMoneyPage = memo(() => (
  <RequireActiveClerkSession resume={{ kind: "transfer" }} requireOccupant>
    <SendingMoneyPageContent />
  </RequireActiveClerkSession>
));

/** Title-case each word: "juan CARLOS" → "Juan Carlos". */
const toTitleCase = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(
      /[^\s-]+/gu,
      (word) => word.charAt(0).toUpperCase() + word.slice(1)
    );

const MIN_TRANSFER_VISIBLE_MS = 2000;

const holdMinimumVisible = (visibleSince: number) => {
  const remaining = MIN_TRANSFER_VISIBLE_MS - (Date.now() - visibleSince);
  if (remaining <= 0) return Promise.resolve();
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, remaining);
  });
};

const isQuoteExpiredError = (err: unknown): boolean =>
  getWidgetApiError(err)?.code === "QUOTE_EXPIRED";

type BindOutcome =
  | { kind: "operation"; operation: IOperationResponse }
  | { kind: "missing" }
  | { kind: "dead" }
  | { kind: "subject-blocked" }
  | { kind: "originator-closed" }
  | { kind: "claim-blocked"; reason: ClaimBlockReason }
  | { kind: "generic"; message: string };

/**
 * One bind per link. StrictMode remounts await the same promise instead of
 * posting twice. A failed attempt clears the slot so confirm can retry.
 * A success stays cached so a remount does not bind again.
 */
let activeBind: { linkId: string; task: Promise<BindOutcome> } | null = null;

const payoutPaymentMethodIdFrom = (
  payment: ReturnType<typeof useStore.getState>["paymentState"]["payment"]
): string | undefined => {
  if (payment?.type === paymentType.CARD) return payment.cardId || undefined;
  if (payment?.type === paymentType.BANK) {
    return payment.bankAccount?.paymentMethodId;
  }
  return undefined;
};

const startBind = (linkId: string): Promise<BindOutcome> => {
  if (activeBind?.linkId === linkId) return activeBind.task;

  const task = (async (): Promise<BindOutcome> => {
    const visibleSince = Date.now();
    const config = useSessionStore.getState().session.data?.config;
    const quote = useStore.getState().quoteState;
    const payment = useStore.getState().paymentState.payment;
    const hintFirst = config?.recipientHint?.firstName?.trim() ?? "";
    const hintLast = config?.recipientHint?.lastName?.trim() ?? "";
    const destinationCountry =
      config?.sendCommitment?.destinationCountry ??
      config?.destinationCountry ??
      "";
    const payoutPaymentMethodId = payoutPaymentMethodIdFrom(payment);

    const bindOnce = (signedQuote: string) =>
      bindDeferredPayout(linkId, {
        signedQuote,
        recipientFirstName: toTitleCase(hintFirst),
        recipientLastName: toTitleCase(hintLast),
        recipientCountry: destinationCountry,
        ...(payoutPaymentMethodId ? { payoutPaymentMethodId } : {}),
      });

    try {
      if (!quote?.signedQuote || !hintFirst || !hintLast || !destinationCountry) {
        return { kind: "missing" };
      }

      let usedQuote: IQuoteResponse = quote;
      let created: IOperationResponse;
      try {
        created = await bindOnce(quote.signedQuote);
      } catch (err) {
        if (
          !isQuoteExpiredError(err) ||
          !quote.capabilityKey ||
          !quote.payoutMethod
        ) {
          throw err;
        }
        usedQuote = await getDeferredQuote(linkId, {
          capabilityKey: quote.capabilityKey,
          payoutMethod: quote.payoutMethod,
        });
        useStore.getState().setQuoteState(usedQuote);
        created = await bindOnce(usedQuote.signedQuote);
      }

      const next = isMockOfacEnabled()
        ? injectOfacActionRequired(created)
        : created;

      safeCapture(getEvents().OPERATION_CREATED, {
        success: true,
        operation_id: next.operationId,
        quote_id: next.quoteId,
        status: next.status,
        send_amount: next.sendAmount,
        receive_amount: next.receiveAmount,
        exchange_rate: next.exchangeRate,
        total_cost: next.totalCost,
        payout_payment_method_id: payoutPaymentMethodId,
        payout_method: usedQuote.payoutMethod,
        capability_key: usedQuote.capabilityKey,
      });
      clearPhoneSkip(linkId);
      await holdMinimumVisible(visibleSince);
      return { kind: "operation", operation: next };
    } catch (err) {
      console.error("[SendingMoneyPage] Bind failed:", err);
      if (isDeadLinkError(err)) return { kind: "dead" };

      safeCapture(getEvents().OPERATION_CREATED, {
        success: false,
        error: err instanceof Error ? err.message : "Bind failed",
        error_code: "OPERATION_CREATE_FAILED",
      });
      clearPhoneSkip(linkId);
      await holdMinimumVisible(visibleSince);

      const apiCode = getWidgetApiError(err)?.code;
      if (
        apiCode === BIND_SUBJECT_BLOCKED_CODE ||
        apiCode === "USER_SUSPENDED"
      ) {
        return { kind: "subject-blocked" };
      }
      const { reason } = parseBindError(err);
      if (isOriginatorClosedReason(reason)) return { kind: "originator-closed" };
      if (reason !== "generic") return { kind: "claim-blocked", reason };
      return {
        kind: "generic",
        message: err instanceof Error ? err.message : "",
      };
    }
  })();

  activeBind = { linkId, task };
  void task.then((outcome) => {
    if (outcome.kind !== "operation" && activeBind?.task === task) {
      activeBind = null;
    }
  });
  return task;
};

const SendingMoneyPageContent = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme, quoteState } = useStore();
  const session = useSessionStore((s) => s.session);
  const { redirectTo } = useRouteWithToken();

  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, dropzoneColors, backgroundIcon } =
    colorConfig;

  const [operation, setOperation] = useState<IOperationResponse | null>(null);

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("committing"));

    const linkId = useSessionStore.getState().session.data?.linkId;
    const config = useSessionStore.getState().session.data?.config;
    const quote = useStore.getState().quoteState;
    const hintFirst = config?.recipientHint?.firstName?.trim() ?? "";
    const hintLast = config?.recipientHint?.lastName?.trim() ?? "";
    const destinationCountry =
      config?.sendCommitment?.destinationCountry ??
      config?.destinationCountry ??
      "";

    if (!linkId || !quote?.signedQuote || !hintFirst || !hintLast || !destinationCountry) {
      redirectTo(APP_ROUTES.TRANSFER_PAGE, { replace: true });
      return;
    }

    let alive = true;
    void startBind(linkId).then((outcome) => {
      if (!alive) return;
      if (outcome.kind === "operation") {
        setOperation(outcome.operation);
        return;
      }
      if (outcome.kind === "missing" || outcome.kind === "dead") {
        if (outcome.kind === "missing") {
          redirectTo(APP_ROUTES.TRANSFER_PAGE, { replace: true });
        }
        return;
      }
      if (outcome.kind === "subject-blocked") {
        redirectTo(APP_ROUTES.ERROR_PAGE, {
          replace: true,
          state: subjectBlockedTerminalState(t),
        });
        return;
      }
      if (outcome.kind === "originator-closed") {
        redirectTo(APP_ROUTES.ERROR_PAGE, {
          replace: true,
          state: originatorClosedTerminalState(t),
        });
        return;
      }
      if (outcome.kind === "claim-blocked") {
        navigate(APP_ROUTES.CLAIM_BLOCKED_PAGE, {
          replace: true,
          state: { reason: outcome.reason },
        });
        return;
      }
      navigate(APP_ROUTES.ERROR_PAGE, {
        state: {
          title: t("transfer.error"),
          subTitle: null,
          message: outcome.message || t("error.tryAgainLater"),
          btnText: t("common.retry"),
          isOut: false,
          pageUrl: APP_ROUTES.TRANSFER_PAGE,
        },
      });
    });

    return () => {
      alive = false;
    };
    // Bind once on mount. Store reads happen inside the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!operation) return;
    if (hasOfacDisambiguation(operation)) {
      redirectTo(
        `${APP_ROUTES.OFAC_DISAMBIGUATION_PAGE}/${operation.operationId}`,
        { replace: true }
      );
      return;
    }
    if (operation.status === "in_review") {
      navigate(APP_ROUTES.CLAIM_BLOCKED_PAGE, {
        replace: true,
        state: { reason: "recipient_review" },
      });
    }
  }, [operation, redirectTo, navigate]);

  const sessionDestinationCurrency =
    session.data?.config?.sendCommitment?.destinationCurrency ??
    session.data?.config?.destinationCurrency;
  const receiveCurrency =
    quoteState?.destinationCurrency ?? sessionDestinationCurrency ?? "USD";

  const displayAmount = operation?.receiveAmount ?? quoteState?.receiveAmount ?? 0;

  const handleDone = () => {
    redirectTo(APP_ROUTES.TRANSACTIONS_PAGE, { replace: true });
  };

  if (operation && (hasOfacDisambiguation(operation) || operation.status === "in_review")) {
    return null;
  }

  if (operation) {
    return (
      <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
        <Layout.Content className="landing-section flow-hero-section">
          <ScrollFadeIndicator color={colorConfig.bodyCopy} />
          <section className="flow-hero">
            <FlowHeroMark
              dropzoneColors={dropzoneColors}
              backgroundIcon={backgroundIcon}
            >
              <RoundedCheckIcon size={60} colors={dropzoneColors} />
            </FlowHeroMark>
            <section className="landing-container">
              <PageTitle
                highlight={t("transfer.transferProcessedTitle")}
                highlightColor={TITLE_COLOR}
                secudnary={t("transfer.transferProcessedSubtitle")}
                secudnaryColor={TITLE_COLOR}
              />
            </section>
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              onClick={handleDone}
            >
              {t("transfer.done")}
            </Button>
          </section>
        </Layout.Footer>
      </AuraLayout>
    );
  }

  return (
    <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        <section className="flow-hero">
          <FlowHeroMark
            showSpinner
            dropzoneColors={dropzoneColors}
            backgroundIcon={backgroundIcon}
          >
            <CashSpecialIcon size={60} colors={dropzoneColors} />
          </FlowHeroMark>
          <section className="landing-container">
            <PageTitle
              highlight={t("payoutClaim.claiming")}
              highlightColor={TITLE_COLOR}
              secudnary={
                displayAmount
                  ? formatLabeledAmount(displayAmount, receiveCurrency)
                  : ""
              }
              secudnaryColor={TITLE_COLOR}
            />
          </section>
        </section>
      </Layout.Content>
    </AuraLayout>
  );
});
